/**
 * @file 教材を追加する 2 つの入口 (名前を入力 / 商品ページの URL か ISBN を貼る)。
 *
 * @what: 名前での追加は「検討中」で即追加。URL か ISBN は openBD から正式な書名と出版社を
 *   取り込んで追加する (ISBN-10 / ISBN-13 / Amazon の /dp/ から ISBN-13 に正規化)。
 *   どちらも「使うステップ」は開いている列の初期値を付ける。
 * @why: 書名は呼び名でなく正式名称で持つ (代表 2026-08-23)。
 * @failure: 書誌が引けないときは文言を出して追加しない。問い合わせは 10 秒で打ち切る。
 */

import { useState } from "react";
import { lookupBook, parseIsbn, productUrl } from "../api/bookLookup";
import { newMaterialId, type StudyMaterialInput } from "../api/plans";
import "./study.css";

const LOOKUP_TIMEOUT_MS = 10_000;

export function MaterialAddForm({
  stageId,
  onAdd,
}: {
  /** 新しい教材の「使うステップ」の初期値。空なら決めない。 */
  stageId: string;
  onAdd: (material: StudyMaterialInput) => void;
}) {
  const [newTitle, setNewTitle] = useState("");
  const [lookupInput, setLookupInput] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);

  const base = (): StudyMaterialInput => ({
    id: newMaterialId(),
    title: "",
    status: "considering",
    unit_ids: [],
    ...(stageId ? { stage_id: stageId } : {}),
  });

  const add = () => {
    const title = newTitle.trim();
    if (!title) return;
    onAdd({ ...base(), title });
    setNewTitle("");
  };

  const addByLookup = async () => {
    const input = lookupInput.trim();
    if (!input || lookupBusy) return;
    const isbn = parseIsbn(input);
    if (!isbn) {
      setLookupError("ISBN を読み取れませんでした。Amazon の商品ページの URL か、ISBN を入れてください。");
      return;
    }
    setLookupBusy(true);
    setLookupError(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), LOOKUP_TIMEOUT_MS);
    try {
      const meta = await lookupBook(isbn, controller.signal);
      if (!meta) {
        setLookupError("この ISBN の書誌が見つかりませんでした。名前を入力して追加してください。");
        return;
      }
      const next: StudyMaterialInput = { ...base(), title: meta.title, isbn: meta.isbn };
      if (meta.publisher) next.publisher = meta.publisher;
      const url = productUrl(input);
      if (url) next.url = url;
      onAdd(next);
      setLookupInput("");
    } catch (err: unknown) {
      console.error("[study] book lookup failed", err);
      setLookupError("書誌を調べられませんでした。時間を置いて再度お試しください。");
    } finally {
      clearTimeout(timer);
      setLookupBusy(false);
    }
  };

  return (
    <div className="study-material-add-block">
      <div className="study-material-add">
        <input
          type="text"
          placeholder="教材の名前（例: はじめてのめいろ 1集）"
          aria-label="追加する教材の名前"
          value={newTitle}
          maxLength={100}
          onChange={(e) => setNewTitle(e.target.value)}
        />
        <button type="button" className="study-save-btn" onClick={add} disabled={!newTitle.trim()}>
          追加
        </button>
      </div>
      <div className="study-material-add study-material-lookup">
        <input
          type="text"
          placeholder="商品ページの URL か ISBN（書名と出版社を調べて追加します）"
          aria-label="追加する教材の URL か ISBN"
          value={lookupInput}
          maxLength={300}
          onChange={(e) => {
            setLookupInput(e.target.value);
            setLookupError(null);
          }}
        />
        <button
          type="button"
          className="study-save-btn"
          onClick={() => void addByLookup()}
          disabled={!lookupInput.trim() || lookupBusy}
        >
          {lookupBusy ? "調べています…" : "調べて追加"}
        </button>
      </div>
      {lookupError ? (
        <p className="study-material-lookup-error" role="alert">
          {lookupError}
        </p>
      ) : null}
    </div>
  );
}
